import {useEffect} from 'react';
import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {usePathname} from '@/hooks/useAppRouter';
import {setFpAuthToken} from '@/lib/linda-jwt';
import {
    lindaChatOpenAtom,
    lindaChatMessagesAtom,
    lindaChatLoadingAtom,
    lindaChatWidthAtom,
    lindaChatProjectAtom,
    addChatMessageAtom,
    clearChatMessagesAtom,
    closeChatAtom,
    saveProjectChatHistory,
    type ChatContext,
} from '@/store/linda-chat';
import {userAtom} from '@/store/user';
import {notificationsAtom} from '@/store/notifications';
import {selectedProductAtom} from '@/store/products';
import {selectedGroupAtom} from '@/store/groups';
import {selectedSupplierIdAtom} from '@/store/retailer-view';

export function useChatState() {
    const [isOpen, setIsOpen] = useAtom(lindaChatOpenAtom);
    const messages = useAtomValue(lindaChatMessagesAtom);
    const [isLoading, setIsLoading] = useAtom(lindaChatLoadingAtom);
    const addMessage = useSetAtom(addChatMessageAtom);
    const clearMessages = useSetAtom(clearChatMessagesAtom);
    const closeChat = useSetAtom(closeChatAtom);
    const [panelWidth, setPanelWidth] = useAtom(lindaChatWidthAtom);

    const user = useAtomValue(userAtom);
    const notifications = useAtomValue(notificationsAtom);
    const selectedProduct = useAtomValue(selectedProductAtom);
    const selectedGroup = useAtomValue(selectedGroupAtom);
    const selectedSupplierId = useAtomValue(selectedSupplierIdAtom);
    const currentChatProject = useAtomValue(lindaChatProjectAtom);

    const pathname = usePathname();

    // Initialize JWT auth for LiteLLM gateway when user loads
    useEffect(() => {
        if (user?.auth_token) {
            setFpAuthToken(user.auth_token);
        }
    }, [user?.auth_token]);

    // Auto-save chat messages to project-specific localStorage when messages change
    useEffect(() => {
        if (currentChatProject && messages.length > 0) {
            saveProjectChatHistory(currentChatProject, messages);
        }
    }, [messages, currentChatProject]);

    // Gather context for Linda
    const getContext = (): ChatContext => {
        const currentAsset = selectedProduct
            ? {
                  id: selectedProduct.id,
                  name: selectedProduct.name,
                  type: 'product' as const,
              }
            : selectedGroup
              ? {
                    id: String(selectedGroup.id),
                    name: selectedGroup.name,
                    type: 'group' as const,
                }
              : undefined;

        // Validate username - only use if it's valid (not empty, not "[username]", not null)
        const firstname = user?.profile?.firstname;
        const isValidUsername =
            firstname &&
            firstname.trim() !== '' &&
            firstname !== '[username]' &&
            firstname.toLowerCase() !== 'username';

        return {
            currentPage: pathname,
            pageType: pathname.includes('/products')
                ? 'product'
                : pathname.includes('/groups')
                  ? 'group'
                  : pathname === '/'
                    ? 'dashboard'
                    : 'other',
            userId: user?.id ? String(user.id) : undefined,
            userName: isValidUsername ? firstname : undefined,
            currentAsset,
        };
    };

    return {
        // State
        isOpen,
        setIsOpen,
        messages,
        isLoading,
        setIsLoading,
        panelWidth,
        setPanelWidth,

        // Actions
        addMessage,
        clearMessages,
        closeChat,

        // Context data
        user,
        notifications,
        selectedProduct,
        selectedGroup,
        selectedSupplierId,

        // Helper
        getContext,
    };
}
